import type { CallRecordListItem } from '@/hooks/use-call-records'
import { getPluginSlots } from '@/plugin/slots'
import { cn } from '@/lib/utils'

/** Fields needed for caller label + initials + triage chip colours. */
export type CallIdentityRow = Pick<
  CallRecordListItem,
  'customer_name' | 'customer_number' | 'call_state'
>

export function callerPrimaryLabel(row: CallIdentityRow): string {
  if (row.customer_name?.trim()) return row.customer_name.trim()
  const n = row.customer_number?.trim()
  if (n) return n
  return 'Caller unknown'
}

/** Stable visual glyph for scanning (decorative; not unique). */
export function callerInitialsLandmark(row: CallIdentityRow): string {
  const name = row.customer_name?.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
      const a = parts[0][0] ?? ''
      const b = parts[1][0] ?? ''
      return (a + b).toUpperCase()
    }
    const single = parts[0] ?? ''
    const letters = single.replace(/[^a-zA-Z]/g, '')
    if (letters.length >= 2) return letters.slice(0, 2).toUpperCase()
    if (letters.length === 1) return letters.toUpperCase()
    if (single.length >= 2) return single.slice(0, 2).toUpperCase()
    if (single.length === 1) return single.toUpperCase()
  }
  const digits = (row.customer_number ?? '').replace(/\D/g, '')
  if (digits.length >= 2) return digits.slice(-2)
  if (digits.length === 1) return `${digits}·`
  return '?'
}

/** Secondary line under the caller name, supplied by the tenant pack (`calls.rowSubtitle`). */
export function callRowSubtitle(row: CallRecordListItem): string | null {
  return getPluginSlots().calls.rowSubtitle?.(row) ?? null
}

const initialsBoxRoutine =
  'border-border bg-muted text-foreground shadow-sm'

const initialsBoxEmergency =
  'border-transparent bg-destructive text-destructive-foreground shadow-sm'

const initialsBoxElevated =
  'border-transparent bg-warning text-warning-foreground shadow-sm'

export function callInitialsBoxClassName(callState: string): string {
  const s = (callState || 'routine').toLowerCase()
  if (s === 'emergency') return initialsBoxEmergency
  if (s === 'elevated') return initialsBoxElevated
  return initialsBoxRoutine
}

/** Strong frame for call-story hero on detail page (emergency / elevated). */
export function callStoryHeroBorderClass(callState: string): string {
  const s = (callState || 'routine').toLowerCase()
  if (s === 'emergency') {
    return 'border-2 border-destructive ring-2 ring-destructive/35 shadow-sm'
  }
  if (s === 'elevated') {
    return 'border-2 border-warning ring-2 ring-warning/30 shadow-sm'
  }
  return 'border border-border shadow-sm'
}

export function CallInitialsLandmark({
  row,
  size = 'card',
}: {
  row: CallIdentityRow
  size?: 'card' | 'hero' | 'row'
}) {
  const callState = (row.call_state || 'routine').toLowerCase()
  const initials = callerInitialsLandmark(row)
  const box = callInitialsBoxClassName(callState)
  const sizeClasses =
    size === 'hero'
      ? 'size-[3.5rem] sm:size-16 p-2 sm:p-2.5'
      : size === 'row'
        ? 'size-10 p-1.5'
        : 'size-[2.75rem] sm:size-[3.25rem] p-1.5 sm:p-2'
  const textClasses =
    size === 'hero'
      ? 'text-4xl sm:text-5xl'
      : size === 'row'
        ? 'text-base'
        : 'text-3xl sm:text-4xl'

  return (
    <div
      className={cn(
        'shrink-0 flex items-center justify-center rounded-lg border',
        sizeClasses,
        box
      )}
      aria-hidden
    >
      <span
        className={cn(
          'font-mono font-bold leading-none tracking-tight',
          textClasses
        )}
      >
        {initials}
      </span>
    </div>
  )
}
